import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import { PaintBlockReportModalStyles, PaintBlockReportSelectLabelStyles } from './styles';
import CloseIcon from '@mui/icons-material/Close';
import {
  ReportActionStatus,
  ReportInternalType,
  ReportType,
  useCreateNewReportMutation,
} from '@/__generated__/graphql';
import { useState } from 'react';
import { enumToString } from '@/utils/string';
import { CustomDateField } from '@/components/shared/CustomField/FieldTemplates/CustomDateField';
import { useRouter } from '@/hooks';
import {
  PAINT_BLOCK_REPORT_DEFAULT_POI_TYPE,
  PoiReportEnumFormattingFilter,
  SUPPORT_ERROR_MESSAGE,
} from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { SmallButton } from '@/components/UserManagement/styles';

type Props = {
  open: boolean;
  onClose: () => void;
  paintRegionId?: string;
};

const REPORT_TYPE_OPTIONS = [ReportType.Inspection, ReportType.Maintenance];
const REPORT_ACTION_STATUS_OPTIONS: ReportActionStatus[] = Object.values(ReportActionStatus);

export const CreatePaintBlockReportModal = ({ open, onClose, paintRegionId }: Props) => {
  const router = useRouter();

  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});

  const [mutate, { loading, data }] = useCreateNewReportMutation();

  const mutationHasFinished = !!data;

  const [formData, setFormData] = useState<{
    type: ReportType;
    actionStatus: ReportActionStatus;
    dueDate: Date;
    name: string;
  }>({
    type: REPORT_TYPE_OPTIONS[0],
    actionStatus: REPORT_ACTION_STATUS_OPTIONS[0],
    dueDate: new Date(),
    name: '',
  });

  const onDraftClick = () => {
    onClose();
  };

  const onSubmit = () => {
    mutate({
      variables: {
        input: {
          structureId: structureId.toString(),
          name: formData.name,
          paintRegionId,
          pointOfInterestType: PAINT_BLOCK_REPORT_DEFAULT_POI_TYPE,
          dueDate: formData.dueDate.toString(),
          type: formData.type,
          actionStatus: formData.actionStatus,
          internalType: ReportInternalType.PaintBlock,
        },
      },
    })
      .then((response) => {
        const newReportid = response?.data?.createNewReport?.id;

        if (response?.data?.createNewReport?.id) {
          showSnackBar('Report created', 'success');
          setTimeout(() => {
            router.push(`/analysis/${structureId}/reports/${newReportid}`);
          }, 1000);
        } else {
          showSnackBar(SUPPORT_ERROR_MESSAGE, 'error');
        }
      })
      .catch((error) => {
        showSnackBar(error?.message || SUPPORT_ERROR_MESSAGE, 'error');
      });
  };

  return (
    <Modal open={open} onClose={onClose} sx={PaintBlockReportModalStyles.modal}>
      <Paper
        sx={{
          ...PaintBlockReportModalStyles.paper,
          px: 3,
          py: 2,
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography fontSize={'20px'} fontWeight={500} mb={2}>
              Add block to create a report
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Box sx={{ height: '100%', my: 1 }}>
            <TextField
              variant="standard"
              label="Report Name"
              fullWidth
              required
              minRows={1}
              maxRows={100}
              onChange={(event) => {
                setFormData((prev) => ({
                  ...prev,
                  name: event.target.value,
                }));
              }}
            />

            <Stack direction={'row'} gap={2} my={5}>
              <FormControl fullWidth>
                <PaintBlockReportSelectLabelStyles id="report-type-select-label">
                  Report Type
                </PaintBlockReportSelectLabelStyles>
                <Select
                  variant="standard"
                  labelId="report-type-select-label"
                  id="report-type-select"
                  label="Report Type"
                  value={formData.type}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: value.target.value as ReportType,
                    }))
                  }
                >
                  {REPORT_TYPE_OPTIONS.map((itemString) => (
                    <MenuItem key={itemString} value={itemString}>
                      {PoiReportEnumFormattingFilter[itemString]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <PaintBlockReportSelectLabelStyles id="report-status-select-label">
                  Status
                </PaintBlockReportSelectLabelStyles>
                <Select
                  variant="standard"
                  labelId="report-action-status-select-label"
                  id="report-action-status-select"
                  label="Report Status"
                  value={formData.actionStatus}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      actionStatus: value.target.value as ReportActionStatus,
                    }))
                  }
                >
                  {REPORT_ACTION_STATUS_OPTIONS.map((itemString) => (
                    <MenuItem key={itemString} value={itemString}>
                      {enumToString(itemString)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={'row'} gap={2} my={5} alignItems={'flex-end'} mb={8}>
              <CustomDateField
                mode="Edit"
                id="due-date"
                title="Due Date"
                sx={{ m: 0 }}
                value={formData.dueDate}
                onFieldValueChange={(value) => {
                  if (value) setFormData((prev) => ({ ...prev, dueDate: value }));
                }}
                format="dd MMM yyyy"
              />
            </Stack>

            <Stack direction={'row'} mt="auto" justifyContent={'flex-end'} gap={1}>
              <SmallButton
                variant="outlined"
                sx={{ textTransform: 'none' }}
                type="button"
                onClick={onDraftClick}
              >
                Close
              </SmallButton>
              <SmallButton
                contained
                variant="contained"
                type="submit"
                sx={{ textTransform: 'none' }}
                disabled={loading || mutationHasFinished}
              >
                Create Report
              </SmallButton>
            </Stack>
          </Box>
        </form>
      </Paper>
    </Modal>
  );
};
